import { useState, useCallback, useRef } from 'react';
import { API } from '../services/clientApi';
import { apiControllers } from '../utils/apiControllers';
import { mapClientFromApi } from '../utils/clientMapper';

/**
 * Cached client list for proposal flow; avoids repeat GETs after first load.
 */
export function useClients() {
  const cacheRef = useRef(null);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchClients = useCallback(async (force = false) => {
    if (!force && cacheRef.current) {
      setClients(cacheRef.current);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const rawList = await API.getAllClient(force ? { skipCache: true } : {});
      const mapped = (Array.isArray(rawList) ? rawList : [])
        .map((row) => mapClientFromApi(row))
        .filter(Boolean);
      cacheRef.current = mapped;
      setClients(mapped);
    } catch (e) {
      setError(e?.message || 'Failed to load clients');
      setClients([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const addClient = useCallback(async (formData) => {
    const payload = apiControllers.getAddClientPayload(formData);
    if (!payload.created_by || !payload.created_by.employee_id) {
      throw new Error('You must be logged in to add a client. Lead Owner (created by) is required.');
    }
    const created = await API.addClient(payload);
    let mapped = mapClientFromApi(created);
    if (!mapped?.companyName) {
      const id = mapped?.id || created?._id || created?.id || created?.data?._id || null;
      mapped = {
        id,
        companyName: payload.client_name,
        address: payload.company_address,
        city: payload.city,
        state: payload.state,
        companyEmail: payload.client_email,
        concernedPersonName: payload.contact_person_name,
        concernedPersonMobile: payload.contact_person_mobile,
        concernedPersonEmail: payload.contact_person_email,
        concernedPersonDesignation: payload.contact_person_designation,
        dateOfDiscussion: payload.discussion_date || '',
        label: [payload.client_name, payload.city, payload.state].filter(Boolean).join(' · ') || payload.client_name,
        raw: created,
      };
    }
    const prev = cacheRef.current || [];
    cacheRef.current = [...prev, mapped];
    setClients(cacheRef.current);
    return mapped;
  }, []);

  return {
    clients,
    loading,
    error,
    fetchClients,
    addClient,
  };
}
